import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CircularProgress from "@mui/material/CircularProgress";
import Heading from "@commons/Typography/Heading";
import Button from "@commons/Button";
import Text from "../Typography/Text";
import { twMerge } from "tailwind-merge";

type ModalProps = {
  open: boolean;
  heading: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  onConfirm?: () => void;
  onEdit?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  className?: string;
  isLoading?: boolean;
  error?: boolean;
};

function Modal({
  open,
  heading,
  children,
  onClose,
  onConfirm,
  onEdit,
  confirmLabel = "Salvar",
  cancelLabel = "Cancelar",
  className = "",
  isLoading = false,
  error = false,
}: ModalProps) {
  if (!open) return null;

  const mergedClasses = twMerge(
    "max-w-6xl w-full bg-white py-6 px-2 rounded-lg shadow-xl flex flex-col max-h-[90vh]",
    className,
  );

  if (isLoading) {
    return (
      <div
        data-testid="modal"
        className="fixed inset-0 backdrop-opacity-80 bg-accent/60 z-50 flex items-center justify-center p-4"
      >
        <div
          className={mergedClasses}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <section className="relative mb-4 px-4 flex items-center justify-center flex-shrink-0">
            <Heading className="w-full xs:text-center xs:pr-0 pr-12">
              {heading}
            </Heading>

            <IconButton
              onClick={onClose}
              className="absolute right-2 h-8 w-8"
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </section>

          <section className="py-8 px-4 flex-grow flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <CircularProgress size={48} className="text-primary" />
              <Text size="md" className="text-gray-600 text-center">
                Carregando dados...
              </Text>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="modal"
        className="fixed inset-0 backdrop-opacity-80 bg-accent/60 z-50 flex items-center justify-center p-4"
      >
        <div
          className={mergedClasses}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <section className="relative mb-4 px-4 flex items-center justify-center flex-shrink-0">
            <Heading className="w-full xs:text-center xs:pr-0 pr-12">
              {heading}
            </Heading>

            <IconButton
              onClick={onClose}
              className="absolute right-2 h-8 w-8"
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </section>

          <section className="py-8 px-4 flex-grow flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 max-w-md">
              <ErrorOutlineIcon
                className="text-red-500"
                sx={{ fontSize: 64 }}
              />
              <Text
                size="lg"
                className="text-gray-800 font-semibold text-center"
              >
                Ops! Algo deu errado
              </Text>
            </div>
          </section>

          <section className="mt-6 flex justify-end gap-3 px-4 flex-shrink-0">
            <Button customVariant="neutral" size="sm" onClick={onClose}>
              <Text size="sm" className="font-roboto font-semibold">
                Fechar
              </Text>
            </Button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="modal"
      className="fixed inset-0 backdrop-opacity-80 bg-accent/60 z-50 flex items-center justify-center p-4"
    >
      <div
        className={mergedClasses}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <section className="relative mb-4 px-4 flex items-center justify-center flex-shrink-0">
          <Heading
            className={`w-full xs:text-center xs:pr-0 ${onEdit ? "pr-20" : "pr-12"}`}
          >
            {heading}
          </Heading>

          {onEdit && (
            <IconButton
              onClick={onEdit}
              className="absolute right-14 h-8 w-8 bg-gray-100 hover:bg-gray-200"
              aria-label="Editar"
            >
              <EditIcon className="text-[20px]" />
            </IconButton>
          )}

          <IconButton
            onClick={onClose}
            className="absolute right-2 h-8 w-8"
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </section>

        <section className="py-1 px-4 overflow-y-auto flex-grow">
          {children}
        </section>

        <section className="mt-6 flex justify-end gap-3 px-4 flex-shrink-0">
          <Button customVariant="neutral" size="sm" onClick={onClose}>
            <Text size="sm" className="font-roboto font-semibold">
              {cancelLabel}
            </Text>
          </Button>
          {onConfirm && (
            <Button size="sm" onClick={onConfirm}>
              <Text size="sm" className="font-roboto font-semibold">
                {confirmLabel}
              </Text>
            </Button>
          )}
        </section>
      </div>
    </div>
  );
}

export default Modal;
